/**
 * Attach an HLS VOD source to a video element.
 *
 * Safari and iOS can play HLS without JavaScript. Other browsers use the
 * bundled hls.js build; no CDN request is made by this module.
 */
import Hls from 'hls.js';

function showPlaybackError(video) {
    if (video.parentNode?.querySelector('[data-hls-playback-error]')) return;
    const message = document.createElement('p');
    message.dataset.hlsPlaybackError = '';
    message.setAttribute('role', 'status');
    message.textContent = 'This video could not be played. Please reload the page or try another browser.';
    video.insertAdjacentElement('afterend', message);
}

export function attachHlsPlayer(video, source) {
    if (!video || !source) return null;

    video.setAttribute('playsinline', '');
    video.dataset.hlsSource = source;
    video.addEventListener('error', () => showPlaybackError(video), { once: true });

    if (video.canPlayType('application/vnd.apple.mpegurl') || video.canPlayType('application/x-mpegURL')) {
        video.src = source;
        return { type: 'native', destroy: () => video.removeAttribute('src') };
    }

    if (!Hls.isSupported()) {
        showPlaybackError(video);
        video.dispatchEvent(new CustomEvent('hls:unsupported', { detail: { source } }));
        return null;
    }

    const hls = new Hls({
        // The proxy supports byte ranges and VOD playlists. Keep the player
        // conservative on older mobile browsers.
        enableWorker: true,
        lowLatencyMode: false,
    });
    hls.loadSource(source);
    hls.attachMedia(video);
    hls.on(Hls.Events.ERROR, (_event, data) => {
        if (data && data.fatal) {
            showPlaybackError(video);
            hls.destroy();
            video.dispatchEvent(new CustomEvent('hls:error', { detail: data }));
        }
    });

    return {
        type: 'hls.js',
        hls,
        destroy: () => hls.destroy(),
    };
}

export function mountHlsPlayers(root = document) {
    const players = [];
    root.querySelectorAll('[data-hls-src]').forEach((video) => {
        if (video.dataset.hlsSource) return;
        const player = attachHlsPlayer(video, video.dataset.hlsSrc);
        if (player) players.push(player);
    });
    return players;
}

if (typeof document !== 'undefined') {
    document.addEventListener('DOMContentLoaded', () => mountHlsPlayers());
}

